import {getScrambleTextAnimation} from '@github-ui/brand-core/utils/animation'
import {XIcon} from '@primer/octicons-react'
import {Button, Heading, Label, Link} from '@primer/react'
import {useEffect, useRef} from 'react'

import type {ProductAnnouncementModalView} from './../../ProductAnnouncement/ProductAnnouncement.types'
import ProductAnnouncementModalTabContent from './../ProductAnnouncementModalTabContent/ProductAnnouncementModalTabContent'
import ProductAnnouncementModalVisual from './../ProductAnnouncementModalVisual/ProductAnnouncementModalVisual'

import ModalStyles from './../ProductAnnouncementModal.module.css'
import Styles from './ProductAnnouncementModalMobile.module.css'

export type ProductAnnouncementModalMobileProps = ProductAnnouncementModalView

const defaultProps: Partial<ProductAnnouncementModalMobileProps> = {}

const ProductAnnouncementModalMobile = (props: ProductAnnouncementModalMobileProps) => {
  const initializedProps: ProductAnnouncementModalMobileProps = {...defaultProps, ...props}
  const {
    currentTabIndex,
    tabs,
    onTabChange,
    shouldShowTabCtas,
    shouldShowMainCta,
    shouldAnimateTitle,
    seeAll,
    cta1,
    cta2,
    title,
    label,
    body,
    onClose,
    aria,
    onEvent,
  } = initializedProps

  const currentTabIndexRef = useRef<number>(currentTabIndex)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const titleTl = useRef<gsap.core.Timeline | null>(null)

  useEffect(() => {
    if (shouldAnimateTitle && currentTabIndexRef.current === -1 && headingRef.current) {
      titleTl.current?.kill()
      titleTl.current = getScrambleTextAnimation(headingRef.current)
    }

    currentTabIndexRef.current = currentTabIndex
  }, [currentTabIndex, shouldAnimateTitle])

  useEffect(() => {
    return () => {
      titleTl.current?.kill()
    }
  }, [])

  return (
    <div className={Styles['ProductAnnouncementModalMobile']}>
      <div>
        <div className={Styles['ProductAnnouncementModalMobile__hero-header']}>
          <div className={Styles['ProductAnnouncementModalMobile__header']}>
            {label && (
              <Label className={Styles['ProductAnnouncementModalMobile__label']} variant="success">
                {label}
              </Label>
            )}

            <Heading
              ref={headingRef}
              className={ModalStyles['ProductAnnouncementModal__title']}
              as="h1"
              variant="medium"
              aria-label={title}
            >
              <span aria-hidden="true">{title}</span>
            </Heading>

            {body && <p className={Styles['ProductAnnouncementModalMobile__body']}>{body}</p>}
          </div>

          <button
            className={ModalStyles['ProductAnnouncementModal__closeButton']}
            onClick={onClose}
            aria-label={aria.close}
          >
            <XIcon />
          </button>
        </div>

        <div className={Styles['ProductAnnouncementModalMobile__hero-visual']}>
          <ProductAnnouncementModalVisual
            currentTabIndex={currentTabIndex}
            visuals={tabs.map(tab => tab.visual)}
            aria={aria}
          />
        </div>
      </div>

      <div className={Styles['ProductAnnouncementModalMobile__content']}>
        <dl className={Styles['ProductAnnouncementModalMobile__content-tabs']}>
          {tabs.map(({title: tabTitle, heading, ...tab}, index) => {
            const isExpanded = index === currentTabIndex
            return (
              <ProductAnnouncementModalTabContent
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`tab-${index}`}
                {...tab}
                title={heading}
                isExpanded={isExpanded}
                onAccordionToggle={() => onTabChange(index)}
                shouldShowTabCtas={!!shouldShowTabCtas}
                onEvent={(event, data = {}) => onEvent?.(event, {...data, tabName: tabTitle, tabIndex: index})}
                onClose={onClose}
              />
            )
          })}
        </dl>
        {seeAll?.href && (
          <div className={Styles['ProductAnnouncementModalMobile__content-seeAll']}>
            <Link
              href={seeAll.href}
              target={seeAll.isNewWindow ? '_blank' : undefined}
              onClick={() => {
                onEvent?.('see_all_clicked')
                if (!seeAll.href) onClose()
              }}
            >
              {seeAll.label}
            </Link>
          </div>
        )}
      </div>

      {shouldShowMainCta && (
        <div className={Styles['ProductAnnouncementModalMobile__ctas']}>
          {cta1 && (
            <Button
              as={cta1.href ? 'a' : 'button'}
              {...(cta1.href ? {href: cta1.href, target: cta1.isNewWindow ? '_blank' : undefined} : {})}
              variant="primary"
              size="large"
              block={!cta2}
              onClick={() => {
                onEvent?.('main_cta1_clicked')
                if (!cta1.href) onClose()
              }}
            >
              {cta1.label}
            </Button>
          )}

          {cta2 && (
            <Button
              as="button"
              size="large"
              block={!cta1}
              onClick={() => {
                onEvent?.('main_cta2_clicked')
                onClose()
              }}
            >
              {cta2.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default ProductAnnouncementModalMobile
